import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
interface ThreeDBackgroundProps {
  variant?: 'hero' | 'metrics' | 'subtle';
}
/**
 * Executive-grade 3D background.
 *
 * Designed to read like an architectural rendering or a financial
 * blueprint at very low opacity — restrained, slow, structural.
 *
 * Composition (back to front):
 *  1. Perspective grid floor (architectural baseline)
 *  2. Three soft gold light pools at corners (depth wash)
 *  3. Hairline depth beams (subtle structural lines)
 *  4. A single, slowly rotating isometric seal/compass (centerpiece)
 *
 * No flying particles, no "globe", no busy motion — boardroom polish.
 */
export function ThreeDBackground({
  variant = 'subtle'
}: ThreeDBackgroundProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [mousePos, setMousePos] = useState({
    x: 0,
    y: 0
  });
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left - rect.width / 2) / rect.width;
      const y = (e.clientY - rect.top - rect.height / 2) / rect.height;
      setMousePos({
        x,
        y
      });
    };
    const node = containerRef.current;
    if (node) node.addEventListener('mousemove', handleMouseMove);
    return () => {
      if (node) node.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);
  const intensity = variant === 'hero' ? 1 : variant === 'metrics' ? 0.7 : 0.55;
  const parallax = variant === 'hero' ? 0.8 : 0.5;
  return <div ref={containerRef} className="absolute inset-0 overflow-hidden pointer-events-none select-none" style={{
    perspective: '1600px',
    perspectiveOrigin: '50% 35%'
  }} data-id="element-290">
      {/* ============================================================
              LAYER 1 — Architectural Perspective Grid Floor
              Receding floor plane, fades elegantly into horizon
              ============================================================ */}
      <div className="absolute left-1/2 bottom-0" style={{
      width: '300%',
      height: '110%',
      transform: `translateX(-50%) rotateX(74deg) translateY(${15 + mousePos.y * 6 * parallax}%) translateX(${mousePos.x * 14 * parallax}px)`,
      transformOrigin: '50% 100%',
      backgroundImage: `
            linear-gradient(rgba(200, 168, 106, ${0.09 * intensity}) 1px, transparent 1px),
            linear-gradient(90deg, rgba(200, 168, 106, ${0.09 * intensity}) 1px, transparent 1px)
          `,
      backgroundSize: '140px 140px',
      maskImage: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.3) 45%, transparent 80%)',
      WebkitMaskImage: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.3) 45%, transparent 80%)'
    }} data-id="element-291" />

      {/* Horizon glow line — where the floor meets the sky */}
      <div className="absolute left-0 right-0" style={{
      top: '52%',
      height: '1px',
      background: `linear-gradient(90deg, transparent 10%, rgba(212, 182, 117, ${0.22 * intensity}) 50%, transparent 90%)`,
      boxShadow: `0 0 ${50 * intensity}px rgba(212, 182, 117, ${0.12 * intensity})`
    }} data-id="element-292" />

      {/* ============================================================
              LAYER 2 — Soft Gold Light Pools (depth wash)
              Three large, very blurred ambient glows
              ============================================================ */}
      <motion.div className="absolute rounded-full" style={{
      top: '-10%',
      left: '-10%',
      width: '60%',
      height: '60%',
      background: 'radial-gradient(circle, rgba(212, 182, 117, 0.12) 0%, transparent 65%)',
      filter: 'blur(40px)',
      opacity: intensity,
      transform: `translateX(${mousePos.x * 20 * parallax}px) translateY(${mousePos.y * 15 * parallax}px)`
    }} animate={{
      scale: [1, 1.08, 1],
      opacity: [intensity * 0.8, intensity, intensity * 0.8]
    }} transition={{
      duration: 14,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-293" />

      <motion.div className="absolute rounded-full" style={{
      bottom: '-15%',
      right: '-10%',
      width: '55%',
      height: '55%',
      background: 'radial-gradient(circle, rgba(200, 168, 106, 0.1) 0%, transparent 65%)',
      filter: 'blur(50px)',
      opacity: intensity,
      transform: `translateX(${mousePos.x * -25 * parallax}px) translateY(${mousePos.y * -18 * parallax}px)`
    }} animate={{
      scale: [1, 1.06, 1],
      opacity: [intensity * 0.7, intensity * 0.95, intensity * 0.7]
    }} transition={{
      duration: 18,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 3
    }} data-id="element-294" />

      <motion.div className="absolute rounded-full" style={{
      top: '35%',
      left: '40%',
      width: '35%',
      height: '35%',
      background: 'radial-gradient(circle, rgba(212, 182, 117, 0.06) 0%, transparent 70%)',
      filter: 'blur(60px)',
      opacity: intensity * 0.8
    }} animate={{
      scale: [1, 1.1, 1]
    }} transition={{
      duration: 12,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 6
    }} data-id="element-295" />

      {/* ============================================================
              LAYER 3 — Architectural Hairline Beams
              Diagonal structural lines suggesting depth and discipline
              ============================================================ */}
      <div className="absolute inset-0" style={{
      opacity: 0.6 * intensity,
      transform: `translateX(${mousePos.x * 8 * parallax}px) translateY(${mousePos.y * 4 * parallax}px)`
    }} data-id="element-296">
        {/* Diagonal left beam */}
        <div className="absolute" style={{
        top: '0',
        left: '18%',
        width: '1px',
        height: '100%',
        background: 'linear-gradient(to bottom, transparent 0%, rgba(200, 168, 106, 0.12) 30%, rgba(200, 168, 106, 0.12) 70%, transparent 100%)',
        transform: 'rotate(6deg)',
        transformOrigin: 'top center'
      }} data-id="element-297" />
        {/* Diagonal right beam */}
        <div className="absolute" style={{
        top: '0',
        right: '14%',
        width: '1px',
        height: '100%',
        background: 'linear-gradient(to bottom, transparent 0%, rgba(212, 182, 117, 0.1) 30%, rgba(212, 182, 117, 0.1) 70%, transparent 100%)',
        transform: 'rotate(-5deg)',
        transformOrigin: 'top center'
      }} data-id="element-298" />
        {/* Horizontal accent */}
        <div className="absolute" style={{
        top: '25%',
        left: '0',
        right: '0',
        height: '1px',
        background: 'linear-gradient(90deg, transparent 10%, rgba(200, 168, 106, 0.08) 50%, transparent 90%)'
      }} data-id="element-299" />
      </div>

      {/* ============================================================
              LAYER 4 — Centerpiece: Isometric Executive Seal
              A single, very slowly rotating compass/seal structure
              Reads as institutional, prestigious, restrained
              ============================================================ */}
      <motion.div className="absolute" style={{
      top: '50%',
      left: '50%',
      width: '560px',
      height: '560px',
      marginLeft: '-280px',
      marginTop: '-280px',
      opacity: 0.7 * intensity,
      transform: `translateX(${mousePos.x * -20 * parallax}px) translateY(${mousePos.y * -12 * parallax}px)`
    }} animate={{
      rotate: [0, 360]
    }} transition={{
      duration: 240,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-300">
        <svg viewBox="0 0 560 560" className="w-full h-full" data-id="element-301">
          <defs data-id="element-302">
            <radialGradient id="sealGlow" cx="50%" cy="50%" r="50%" data-id="element-303">
              <stop offset="0%" stopColor="rgba(212, 182, 117, 0.04)" data-id="element-304" />
              <stop offset="100%" stopColor="rgba(212, 182, 117, 0)" data-id="element-305" />
            </radialGradient>
          </defs>

          {/* Inner glow */}
          <circle cx="280" cy="280" r="240" fill="url(#sealGlow)" data-id="element-306" />

          {/* Outer concentric rings */}
          <circle cx="280" cy="280" r="260" fill="none" stroke="rgba(200, 168, 106, 0.14)" strokeWidth="0.5" data-id="element-307" />
          <circle cx="280" cy="280" r="240" fill="none" stroke="rgba(200, 168, 106, 0.1)" strokeWidth="0.5" strokeDasharray="2 6" data-id="element-308" />
          <circle cx="280" cy="280" r="180" fill="none" stroke="rgba(212, 182, 117, 0.12)" strokeWidth="0.5" data-id="element-309" />
          <circle cx="280" cy="280" r="120" fill="none" stroke="rgba(200, 168, 106, 0.1)" strokeWidth="0.5" data-id="element-310" />

          {/* 12 radial tick marks (clock/compass) */}
          {Array.from({
          length: 12
        }).map((_, i) => {
          const angle = i * 30 * Math.PI / 180;
          const isMajor = i % 3 === 0;
          const inner = isMajor ? 230 : 245;
          const outer = 260;
          const x1 = 280 + inner * Math.cos(angle);
          const y1 = 280 + inner * Math.sin(angle);
          const x2 = 280 + outer * Math.cos(angle);
          const y2 = 280 + outer * Math.sin(angle);
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={`rgba(212, 182, 117, ${isMajor ? 0.25 : 0.12})`} strokeWidth={isMajor ? 0.8 : 0.5} data-id="element-311" />;
        })}

          {/* Cardinal cross */}
          <line x1="280" y1="20" x2="280" y2="540" stroke="rgba(200, 168, 106, 0.08)" strokeWidth="0.5" data-id="element-312" />
          <line x1="20" y1="280" x2="540" y2="280" stroke="rgba(200, 168, 106, 0.08)" strokeWidth="0.5" data-id="element-313" />

          {/* Inner hexagon — architectural seal */}
          <polygon points="280,170 375,225 375,335 280,390 185,335 185,225" fill="none" stroke="rgba(212, 182, 117, 0.18)" strokeWidth="0.7" data-id="element-314" />

          {/* Innermost diamond */}
          <polygon points="280,240 320,280 280,320 240,280" fill="none" stroke="rgba(212, 182, 117, 0.22)" strokeWidth="0.6" data-id="element-315" />
        </svg>
      </motion.div>

      {/* ============================================================
              LAYER 5 — Counter-rotating outer ring (very slow)
              Adds dimensional depth without busyness
              ============================================================ */}
      <motion.div className="absolute" style={{
      top: '50%',
      left: '50%',
      width: '720px',
      height: '720px',
      marginLeft: '-360px',
      marginTop: '-360px',
      opacity: 0.45 * intensity,
      transform: `translateX(${mousePos.x * 12 * parallax}px) translateY(${mousePos.y * 8 * parallax}px)`
    }} animate={{
      rotate: [0, -360]
    }} transition={{
      duration: 360,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-316">
        <svg viewBox="0 0 720 720" className="w-full h-full" data-id="element-317">
          <circle cx="360" cy="360" r="340" fill="none" stroke="rgba(200, 168, 106, 0.08)" strokeWidth="0.4" strokeDasharray="1 12" data-id="element-318" />
        </svg>
      </motion.div>
    </div>;
}